import { TextInput } from "components/common";
import { WithErrorForm } from "components/common/WithErrorForm";
import { Button } from "components/parts/Button/Button";
import { TextWithIconButton } from "components/parts/Button/TextWithIconButton";
import { TextWithIconTypes } from "components/parts/Button/TextWithIconButton/TextWithIconButton";
import { useFormik } from "formik";
import { useLoadingState } from "providers/LoadingProvider";
import React from "react";
import { useHistory } from "react-router";
import { sendConfirmationCode, setPassword } from "utils/auth";
import { validationSchemaConfirm } from "utils/validation";

import { Typography } from "../../../components/common/Typography/index";
import {
  StyledCard,
  StyledForm,
  StyledFormFooter,
  StyledFormHeader,
  StyledFormRow,
  StyledLayout,
} from "./styled";

type UserConfirmFormValueType = {
  code: string;
  email: string;
  password: string;
  passwordConfirm: string;
};

const INITIAL_VALUES = {
  code: "",
  email: "",
  password: "",
  passwordConfirm: "",
};

export const Confirm: React.FC = () => {
  const history = useHistory();
  const { setLoading } = useLoadingState();

  const onSubmit = async (values: UserConfirmFormValueType) => {
    try {
      setLoading(true);
      const result = await setPassword(
        values.email,
        values.password,
        values.code
      );
      if (result === "InitialCertification") {
        alert("初回認証に成功しました。");
        history.replace("/auth/login");
        return;
      } else if (result === "ChangePassword") {
        alert("パスワードを変更しました。");
        history.replace("/auth/login");
        return;
      }

      switch (result) {
        case "Required properties: email, password and confirmation_code":
          alert(
            "メールアドレス、パスワード、認証コードのいづれかが入力されていません。"
          );
          break;

        case "The email doesn't exist":
          alert("メールアドレスが有効ではありません。");
          break;

        case "The user not found":
          alert(
            "入力されたメールアドレスをもつ有効なユーザーが見つかりません。"
          );
          break;

        case "InvalidPasswordException":
          // ユーザープールのポリシーで設定したパスワードの強度を満たさない場合に起こる。
          alert("パスワードの強度が足りません。");
          break;

        case "InvalidParameterException":
          // password が6文字未満など Cognito 側で正しくパースできない場合（バリデーションエラー）に起こる。
          alert("有効なパスワードではありません。");
          break;

        case "CodeMismatchException":
          // 無効なコードが入力された場合に起こる。
          alert("コードが間違っています。");
          break;

        case "LimitExceededException":
          // コードを間違え続けた場合に起こる。
          alert("連続してコードを間違えました。");
          break;

        case "ExpiredCodeException":
          // コードが期限切れ（24時間をオーバー）した場合に起こる。
          // 注) username が存在しない・無効化されている場合にも起こる。
          alert("コードが期限切れ、またはアカウントが既に削除されています。");
          break;

        case "NotAuthorizedException":
          // 既にステータスが CONFIRMED になっている場合に起こる。
          alert("既に認証済みです。");
          break;

        default:
      }
    } catch (e) {
      console.error(e);
      alert("処理に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  const onClickResendButton = async (email: string) => {
    try {
      setLoading(true);
      const result = await sendConfirmationCode(email);
      if (result === "Succeeded") {
        alert("確認コードをメールで送付しました。");
        return;
      }
      switch (result) {
        case "Required properties: email":
          alert("メールアドレスを入力してください。");
          break;

        case "The email doesn't exist":
          alert("メールアドレスが有効ではありません。");
          break;

        case "CodeDeliveryFailureException":
          // 検証コードの送信に失敗した場合に起こる。
          alert("コードの送信に失敗しました。");
          break;

        default:
      }
    } catch (e) {
      console.error(e);
      alert("コードの送信に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik<UserConfirmFormValueType>({
    initialValues: INITIAL_VALUES,
    validationSchema: validationSchemaConfirm,
    onSubmit,
  });

  return (
    <StyledLayout>
      <StyledCard>
        <StyledFormHeader>
          <Typography text="ユーザー確認" fontSize="large" />
        </StyledFormHeader>
        <StyledForm onSubmit={formik.handleSubmit}>
          <StyledFormRow>
            <Typography text="メールアドレス" className="form-label" />
            <WithErrorForm
              touched={formik.touched.email}
              errorMessage={formik.errors.email}
            >
              <TextInput
                width="20rem"
                type="email"
                maxlength="50"
                {...formik.getFieldProps("email")}
              />
            </WithErrorForm>
          </StyledFormRow>
          <StyledFormRow>
            <Typography text="確認コード" className="form-label" />
            <WithErrorForm
              touched={formik.touched.code}
              errorMessage={formik.errors.code}
            >
              <TextInput
                width="20rem"
                type="text"
                maxlength="6"
                {...formik.getFieldProps("code")}
              />
            </WithErrorForm>
          </StyledFormRow>
          <StyledFormRow>
            <Typography text="パスワード" className="form-label" />
            <WithErrorForm
              touched={formik.touched.password}
              errorMessage={formik.errors.password}
            >
              <TextInput
                width="20rem"
                type="password"
                maxlength="15"
                {...formik.getFieldProps("password")}
              />
            </WithErrorForm>
          </StyledFormRow>
          <StyledFormRow>
            <Typography text="パスワード再入力" className="form-label" />
            <WithErrorForm
              touched={formik.touched.passwordConfirm}
              errorMessage={formik.errors.passwordConfirm}
            >
              <TextInput
                width="20rem"
                type="password"
                maxlength="15"
                {...formik.getFieldProps("passwordConfirm")}
              />
            </WithErrorForm>
          </StyledFormRow>

          <StyledFormFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                onClickResendButton(formik.values.email);
              }}
              text="確認コード再送"
            />
            <TextWithIconButton
              type="submit"
              variant="primary"
              text="確認"
              iconName={TextWithIconTypes.ExternalLinkOutline}
            />
          </StyledFormFooter>
        </StyledForm>
      </StyledCard>
    </StyledLayout>
  );
};
